import { ErrorCodes, getErrorMessage } from "./define/define";
import { writable } from "svelte/store";

// 상수 및 타입 정의
const API_ERROR_MESSAGE = "오류가 발생했습니다. 다시 시도해 주세요.";
const AUTH_ERROR_CODES = [ErrorCodes.EMPTY_TOKEN, ErrorCodes.EXPIRED_TOKEN, ErrorCodes.INVALID_TOKEN];

const initListValues = {
	resultCode: 0,
	resultDescKo: "",
	data: [],
};

// 서버 목록 정의
export const SERVER_LIST = [
	{ value: "localhost:8090", label: "Localhost", env: "local" },
	{ value: "dev-api-backstage.sleepwave.ai", label: "Development", env: "dev" },
	{ value: "stg-api-backstage.sleepwave.ai", label: "Staging", env: "stg" },
	{ value: "kr-api-backstage.sleepwave.ai", label: "Production - KR", env: "kr" },
	{ value: "us-api-backstage.sleepwave.ai", label: "Production - US", env: "us" },
	{ value: "dev-api-backstage.blaupunkt-solum.cn", label: "China DEV", env: "cn" },
	{ value: "api-backstage.blaupunkt-solum.cn", label: "China PRD", env: "cn" },
];

// 서버별 토큰 관리 스토어
export const serverTokens = writable({});
export const currentServer = writable("");

// 서버별 토큰 유틸리티
export const tokenManager = {
	// 모든 서버 토큰 저장
	saveAllTokens(tokens) {
		sessionStorage.setItem("AllServerTokens", JSON.stringify(tokens));
		serverTokens.set(tokens);
	},

	// 모든 서버 토큰 로드
	loadAllTokens() {
		const stored = sessionStorage.getItem("AllServerTokens");
		if (stored) {
			const tokens = JSON.parse(stored);
			serverTokens.set(tokens);
			return tokens;
		}
		return {};
	},

	// 특정 서버 토큰 가져오기
	getServerToken(serverUrl) {
		const tokens = this.loadAllTokens();
		return tokens[serverUrl] || null;
	},

	// 현재 활성 서버 토큰 가져오기
	getCurrentToken() {
		const server = localStorage.getItem("selectedServer");
		return this.getServerToken(server);
	},

	// 서버 변경 시 토큰 업데이트
	switchServer(serverUrl) {
		const token = this.getServerToken(serverUrl);
		if (token) {
			sessionStorage.setItem("AccessToken", token);
			localStorage.setItem("selectedServer", serverUrl);
			currentServer.set(serverUrl);
			return true;
		}
		return false;
	},

	// 모든 토큰 삭제 (로그아웃)
	clearAllTokens() {
		sessionStorage.removeItem("AllServerTokens");
		sessionStorage.removeItem("AccessToken");
		serverTokens.set({});
	},

	// 로그인된 서버 목록 가져오기
	getLoggedInServers() {
		const tokens = this.loadAllTokens();
		return Object.keys(tokens).filter((server) => tokens[server]);
	},
};

// 인증 관련 유틸리티
const authUtils = {
	clearSession() {
		tokenManager.clearAllTokens();
		self.location.href = "/";
	},

	isAuthError(code) {
		return AUTH_ERROR_CODES.includes(code);
	},
};

// API 에러 처리
const handleApiError = (error) => {
	if (!error?.resultCode) {
		alert(API_ERROR_MESSAGE);
		return false;
	}

	if (error.resultCode === ErrorCodes.OK) {
		return true;
	}

	// 에러 메시지 먼저 출력
	alert(error.resultDescKo || getErrorMessage(error.resultCode));

	if (authUtils.isAuthError(error.resultCode)) {
		authUtils.clearSession();
	}

	return false;
};

// API 스토어 생성 팩토리
const createApiStore = () => {
	const store = writable(initListValues);

	return {
		subscribe: store.subscribe,
		reset: () => store.set(initListValues),
		fetch: async (url, data = null) => {
			try {
				// 동적으로 API 함수 import
				const { getApi } = await import("./service/api");
				const response = await getApi(url, data);
				console.log(response);
				if (handleApiError(response)) {
					store.set(response);
					return true;
				}
			} catch (error) {
				handleApiError({});
			}
			return false;
		},
	};

	const clear = () => {
		store.set(initListValues);
	};
};

// 네비게이션 관련 스토어
export const navigationStores = {
	menu: writable(""),
	menuSub: writable(""),
	menuSubSub: writable(""),
};

// 체크박스 관련 스토어
export const checkStores = {
	list: writable([]),
	all: writable(false),
};

// API 스토어
export const apiStores = {
	file: createApiStore(),
	putFile: createApiStore(),

	categoryAll: createApiStore(),
	musicSleepsAll: createApiStore(),
	meditationMusicsAll: createApiStore(),
	musicFocusAll: createApiStore(),
	soundSourcesAll: createApiStore(),
	soundMusicsAll: createApiStore(),
	soundMusicsGen2All: createApiStore(),
	soundImagesGen2All: createApiStore(),
	categoryGen2All: createApiStore(),
	appVersionAll: createApiStore(),
	algorithmVersionAll: createApiStore(),
	firmwareVersionAll: createApiStore(),
	firmwareVersionGen2All: createApiStore(),

	deviceDistributionAll: createApiStore(),
	deviceSerialAll: createApiStore(),
	adminAll: createApiStore(),
	logApiAll: createApiStore(),
	logApiTableAll: createApiStore(),
	logAppAll: createApiStore()
};

// 이전 export 이름과의 호환성 유지
export const { menu, menuSub, menuSubSub } = navigationStores;
export const { list: checkedList, all: check } = checkStores;
export const { categoryAll } = apiStores;
export const { musicSleepsAll } = apiStores;
export const { meditationMusicsAll } = apiStores;
export const { musicFocusAll } = apiStores;
export const { soundSourcesAll } = apiStores;
export const { soundMusicsAll } = apiStores;
export const { soundMusicsGen2All } = apiStores;
export const { soundImagesGen2All } = apiStores;
export const { categoryGen2All } = apiStores;
export const { appVersionAll } = apiStores;
export const { algorithmVersionAll } = apiStores;
export const { firmwareVersionAll } = apiStores;
export const { firmwareVersionGen2All } = apiStores;
export const { deviceDistributionAll } = apiStores;
export const { deviceSerialAll } = apiStores;
export const { adminAll } = apiStores;
export const { logApiAll } = apiStores;
export const { logApiTableAll } = apiStores;
export const { logAppAll } = apiStores;

// API 서버 URL store
const storedServer = localStorage.getItem("selectedServer") || "dev-api-backstage.sleepwave.ai";
export const apiServer = writable(storedServer);

// 서버 URL 변경 시 localStorage에도 저장
apiServer.subscribe((value) => {
	if (typeof window !== "undefined") {
		localStorage.setItem("selectedServer", value);
	}
});
